import React, { useEffect, useRef, useCallback } from 'react';
import { Map, Marker, Popup, StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Coordinate } from '../../../../dataTypes/geolocationManagment/types';
import styles from './OpenStreetMapV2.module.scss';

/* =========================
   Styles de carte prêts à l'emploi
========================= */
export const MAP_STYLES = {
  street: 'https://tiles.openfreemap.org/styles/liberty',
  dark:   'https://tiles.openfreemap.org/styles/dark',
  bright: 'https://tiles.openfreemap.org/styles/bright',
} as const;

/* =========================
   Couleurs de carte custom
========================= */
export interface MapColors {
  water?:      string;
  land?:       string;
  landcover?:  string;
  road?:       string;
  building?:   string;
  labelText?:  string;
  labelHalo?:  string;
}

/* =========================
   Props
========================= */
/**
 * Marqueur piloté par l'appelant — voie distincte de `coordinates`, qui reste la
 * voie « statistiques » (points anonymes, agrégés, non cliquables, rendus en
 * heatmap en mode sombre).
 *
 * Ici chaque point a une identité, peut être sélectionné et porte éventuellement
 * une direction de visée. C'est ce qu'il faut pour une carte d'exploitation, où
 * l'on désigne UN appareil — pas pour compter une audience.
 */
export interface MapMarker {
  id:        string;
  lat:       number;
  lng:       number;
  label?:    string;
  color?:    string;
  /** Cap de visée, 0-360° depuis le nord. Trace un cône d'orientation si fourni. */
  heading?:  number | null;
  selected?: boolean;
}

interface OpenStreetMapV2Props {
  coordinates:     Coordinate[];
  width?:          string;
  height?:         string;
  initialZoom?:    number;
  renderMode?:     'street' | 'dark';
  mapStyle?:       string | StyleSpecification;
  mapColors?:      MapColors;
  cityLabelsOnly?: boolean;
  /**
   * Quand il est fourni, il REMPLACE `coordinates` au rendu : ni pins de
   * statistiques, ni heatmap. Les deux voies ne se superposent jamais.
   */
  markers?:        MapMarker[];
  onMarkerClick?:  (id: string) => void;
  /**
   * Recentrage automatique sur le barycentre à chaque changement de points.
   * Vrai par défaut (comportement historique du tableau de bord) ; à couper pour
   * une carte vivante, où la vue sauterait à chaque relevé GPS.
   */
  autoCenter?:     boolean;
  /**
   * Prévenu quand le curseur passe à proximité d'un point de `coordinates`
   * (index dans ce tableau), puis `null` quand il s'en éloigne.
   */
  onPointHover?:   (index: number | null) => void;
  /**
   * Clic sur la carte : index du point de `coordinates` le plus proche (18 px), ou
   * `null` pour un clic dans le vide. L'appelant marque le sien `selected`.
   */
  onPointClick?:   (index: number | null) => void;
  /**
   * Avec `autoCenter` : cadre la vue sur l'ensemble des points (zoom ajusté) au lieu
   * de se contenter de centrer sur leur barycentre.
   */
  fitPoints?:      boolean;
  /** Où centrer quand il n'y a aucun point à afficher (la vue garde son zoom). */
  emptyCenter?:    { lat: number; lng: number } | null;
  /**
   * La molette ne zoome qu'après un clic dans la carte, et jusqu'à ce que la souris
   * en sorte : avant, elle fait défiler la page (Dashboard, 2026-10-22).
   */
  clickToZoom?:    boolean;
}

/* =========================
   Helpers
========================= */

/**
 * Masque tous les layers de labels sauf les villes/communes.
 * Conserve : city, town, village, suburb, quarter, neighbourhood
 * Masque  : country, state, region, county, continent, ocean, sea, bay, lake...
 */
function applyCityLabelsOnly(map: Map) {
  const style = map.getStyle();
  if (!style?.layers) return;

  // Patterns des labels à MASQUER (régions, pays, plans d'eau, etc.)
  const HIDDEN_PATTERNS = [
    /country/i,
    /state/i,
    /region/i,
    /county/i,
    /continent/i,
    /ocean/i,
    /^sea/i,
    /bay/i,
    /lake/i,
    /island/i,
    /border/i,
    /boundary/i,
  ];

  // Patterns des labels à CONSERVER (villes et infra urbaine)
  const VISIBLE_PATTERNS = [
    /city/i,
    /town/i,
    /village/i,
    /suburb/i,
    /quarter/i,
    /neighbourhood/i,
    /place[-_]city/i,
    /place[-_]town/i,
  ];

  style.layers.forEach(layer => {
    if (layer.type !== 'symbol') return;

    const isLabel =
      'layout' in layer &&
      (layer.layout as Record<string, unknown>)?.['text-field'] !== undefined;
    if (!isLabel) return;

    const id = layer.id;

    const shouldHide = HIDDEN_PATTERNS.some(p => p.test(id));
    const shouldShow = VISIBLE_PATTERNS.some(p => p.test(id));

    try {
      if (shouldHide && !shouldShow) {
        map.setLayoutProperty(id, 'visibility', 'none');
      }
      // Les layers qui ne matchent aucun pattern restent tels quels (routes, POI...)
    } catch (_) {}
  });
}

function hexToRgbArray(hex: string): [number, number, number] {
  const s = hex.replace('#', '');
  return [
    parseInt(s.slice(0, 2), 16),
    parseInt(s.slice(2, 4), 16),
    parseInt(s.slice(4, 6), 16),
  ];
}

function applyMapColors(map: Map, colors: MapColors) {
  const style = map.getStyle();
  if (!style?.layers) return;

  const LAYER_PATTERNS: Record<keyof MapColors, RegExp> = {
    water:      /water/i,
    land:       /^(land|background|landuse)$/i,
    landcover:  /landcover|landuse/i,
    road:       /road|street|path|tunnel|bridge/i,
    building:   /building/i,
    labelText:  /label|place|poi|text/i,
    labelHalo:  /label|place|poi|text/i,
  };

  style.layers.forEach(layer => {
    if (!('paint' in layer)) return;

    (Object.keys(colors) as (keyof MapColors)[]).forEach(key => {
      const color = colors[key];
      if (!color) return;
      if (!LAYER_PATTERNS[key].test(layer.id)) return;

      try {
        if (key === 'labelHalo' && layer.type === 'symbol') {
          map.setPaintProperty(layer.id, 'text-halo-color', color);
        } else if (key === 'labelText' && layer.type === 'symbol') {
          map.setPaintProperty(layer.id, 'text-color', color);
        } else if (layer.type === 'fill') {
          map.setPaintProperty(layer.id, 'fill-color', color);
        } else if (layer.type === 'line') {
          map.setPaintProperty(layer.id, 'line-color', color);
        } else if (layer.type === 'background') {
          map.setPaintProperty(layer.id, 'background-color', color);
        }
      } catch (_) {}
    });
  });
}

function drawHeatmapCanvas(
  canvas: HTMLCanvasElement,
  map: Map,
  coordinates: Coordinate[]
) {
  const container = map.getContainer();
  canvas.width  = container.clientWidth;
  canvas.height = container.clientHeight;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  coordinates.forEach(coord => {
    const pt = map.project([coord.lng, coord.lat]);
    const color = coord.color ?? '#ff3b3b';
    const [r, g, b] = hexToRgbArray(color);

    const halo = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, 36);
    halo.addColorStop(0,   `rgba(${r},${g},${b},0.55)`);
    halo.addColorStop(0.4, `rgba(${r},${g},${b},0.18)`);
    halo.addColorStop(1,   `rgba(${r},${g},${b},0)`);
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 36, 0, Math.PI * 2);
    ctx.fillStyle = halo;
    ctx.fill();

    const core = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, 6);
    core.addColorStop(0,   `rgba(${r},${g},${b},1)`);
    core.addColorStop(0.5, `rgba(${r},${g},${b},0.85)`);
    core.addColorStop(1,   `rgba(${r},${g},${b},0)`);
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 6, 0, Math.PI * 2);
    ctx.fillStyle = core;
    ctx.fill();

    if (coord.selected) {
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 11, 0, Math.PI * 2);
      ctx.lineWidth   = 2.5;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();
    }
  });
}

/* =========================
   Composant principal
========================= */
export const OpenStreetMapV2 = ({
  coordinates,
  width          = '100%',
  height         = '100%',
  initialZoom    = 4,
  renderMode     = 'street',
  mapStyle,
  mapColors,
  cityLabelsOnly = false,
  markers,
  onMarkerClick,
  autoCenter     = true,
  onPointHover,
  onPointClick,
  fitPoints      = false,
  emptyCenter,
  clickToZoom    = false,
}: OpenStreetMapV2Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<Map | null>(null);
  const markersRef   = useRef<Marker[]>([]);
  const canvasRef    = useRef<HTMLCanvasElement | null>(null);

  // ── FIX : ref toujours à jour pour éviter les closures périmées ──────────────
  const coordinatesRef = useRef<Coordinate[]>(coordinates);
  useEffect(() => {
    coordinatesRef.current = coordinates;
  }, [coordinates]);

  const markersDataRef = useRef<MapMarker[] | undefined>(markers);
  useEffect(() => { markersDataRef.current = markers; }, [markers]);

  const emptyCenterRef = useRef(emptyCenter);
  emptyCenterRef.current = emptyCenter;

  const onPointHoverRef = useRef(onPointHover);
  useEffect(() => { onPointHoverRef.current = onPointHover; }, [onPointHover]);
  const onPointClickRef = useRef(onPointClick);
  useEffect(() => { onPointClickRef.current = onPointClick; }, [onPointClick]);

  const onMarkerClickRef = useRef(onMarkerClick);
  useEffect(() => { onMarkerClickRef.current = onMarkerClick; }, [onMarkerClick]);

  /* ---- résolution du style ---- */
  const resolvedStyle = mapStyle ?? (
    renderMode === 'dark' ? MAP_STYLES.dark : MAP_STYLES.street
  );

  /* ---- centre calculé depuis les coordonnées ---- */
  const getCenter = useCallback((): [number, number] => {
    const pts: { lat: number; lng: number }[] =
      markersDataRef.current ?? coordinatesRef.current;
    if (pts.length === 0) return emptyCenterRef.current ? [emptyCenterRef.current.lng, emptyCenterRef.current.lat] : [2.33, 47.08];
    const avgLng = pts.reduce((s, c) => s + c.lng, 0) / pts.length;
    const avgLat = pts.reduce((s, c) => s + c.lat, 0) / pts.length;
    return [avgLng, avgLat];
  }, []); // pas de dépendance sur coordinates : on lit depuis la ref

  /* ---- dessin du canvas heatmap ---- */
  // Lit coordinatesRef.current à l'exécution → toujours à jour, même depuis
  // les listeners map (move/zoom/resize) enregistrés une seule fois à l'init.
  const redrawCanvas = useCallback(() => {
    if (!canvasRef.current || !mapRef.current || renderMode !== 'dark') return;
    // Voie pilotée : pas de heatmap, les marqueurs se suffisent (et se superposer
    // aux halos rendrait les points impossibles à viser).
    if (markersDataRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      return;
    }
    drawHeatmapCanvas(canvasRef.current, mapRef.current, coordinatesRef.current);
  }, [renderMode]); // plus de dépendance sur coordinates

  /* ---- placement des markers SVG (mode street) ---- */
  // Idem : lit coordinatesRef.current pour avoir les données fraîches.
  const syncMarkers = useCallback(() => {
    if (!mapRef.current) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    // ── Voie pilotée ────────────────────────────────────────────────────────
    // Indépendante du renderMode : une carte d'exploitation reste cliquable, y
    // compris sur le fond sombre du tableau de bord.
    const piloted = markersDataRef.current;
    if (piloted) {
      piloted.forEach(mk => {
        const color = mk.color ?? '#22d3ee';
        const el    = document.createElement('div');
        el.className = styles.markerWrapper;
        el.style.cursor = 'pointer';
        // Le cône dit où pointe l'objectif ; sans cap connu on n'en dessine pas,
        // plutôt que d'en inventer un qui serait pris pour une mesure.
        const cone = mk.heading == null ? '' : `
          <g transform="rotate(${mk.heading} 22 22)">
            <path d="M22 22 L11 -2 A24 24 0 0 1 33 -2 Z" fill="${color}" opacity="${mk.selected ? 0.45 : 0.22}" />
          </g>`;
        el.innerHTML = `
          <svg viewBox="0 0 44 44" width="44" height="44"
               xmlns="http://www.w3.org/2000/svg" style="overflow:visible;">
            ${cone}
            <circle cx="22" cy="22" r="${mk.selected ? 9 : 6}" fill="${color}"
                    stroke="#0b1220" stroke-width="2" />
            ${mk.selected ? `<circle cx="22" cy="22" r="14" fill="none" stroke="${color}" stroke-width="2" opacity="0.8"/>` : ''}
          </svg>`;
        el.title = mk.label ?? mk.id;
        el.addEventListener('click', ev => {
          ev.stopPropagation();
          onMarkerClickRef.current?.(mk.id);
        });

        const marker = new Marker({ element: el, anchor: 'center' })
          .setLngLat([mk.lng, mk.lat])
          .addTo(mapRef.current!);
        markersRef.current.push(marker);
      });
      return;
    }

    if (renderMode !== 'street') return;

    coordinatesRef.current.forEach(coord => {
      const color = coord.color ?? '#ff3b3b';
      const el = document.createElement('div');
      el.className = styles.markerWrapper;
      el.innerHTML = `
        <svg viewBox="0 0 28 42" width="28" height="42"
             xmlns="http://www.w3.org/2000/svg" style="overflow:visible;">
          <path d="M14 0C6 0 0 6 0 14c0 10 14 28 14 28s14-18 14-28C28 6 22 0 14 0z"
                fill="${color}" />
          <circle cx="14" cy="14" r="5" fill="white"/>
        </svg>`;

      const popup = new Popup({ offset: [0, -36], closeButton: false })
        .setText([coord.info1, coord.info2].filter(Boolean).join(', '));

      const marker = new Marker({ element: el, anchor: 'bottom' })
        .setLngLat([coord.lng, coord.lat])
        .setPopup(popup)
        .addTo(mapRef.current!);

      markersRef.current.push(marker);
    });
  }, [renderMode]); // plus de dépendance sur coordinates

  /* ---- cadrage de la vue sur les points ---- */
  // Relue à chaque rendu (props fraîches) et appelée aussi depuis les écouteurs de la
  // carte, enregistrés une seule fois : d'où le passage par `cadrerRef`.
  const cadrerRef = useRef<((animer: boolean) => void) | null>(null);
  const utilisateurABouge = useRef(false);

  cadrerRef.current = (animer: boolean) => {
    const map = mapRef.current;
    if (!map || !autoCenter) return;
    utilisateurABouge.current = false;

    const pts = markersDataRef.current ?? coordinatesRef.current;

    if (fitPoints && pts.length > 0) {
      const lngs = pts.map(c => c.lng), lats = pts.map(c => c.lat);
      // Le zoom suit l'étalement des points : proches, on se rapproche (jusqu'à 11,
      // une agglomération) ; éloignés, on recule pour tous les garder. Un point seul
      // (ou plusieurs au même endroit) : un niveau « ville ».
      const etale = Math.max(...lngs) - Math.min(...lngs) > 1e-4 || Math.max(...lats) - Math.min(...lats) > 1e-4;
      if (etale) {
        map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 60, maxZoom: 11, animate: animer },
        );
      } else if (animer) {
        map.flyTo({ center: [lngs[0], lats[0]], zoom: 9 });
      } else {
        map.jumpTo({ center: [lngs[0], lats[0]], zoom: 9 });
      }
    } else if (animer) {
      map.flyTo({ center: getCenter() });
    } else {
      map.jumpTo({ center: getCenter() });
    }
  };

  /* ---- initialisation de la carte ---- */
  useEffect(() => {
    if (!containerRef.current) return;

    const map = new Map({
      container:          containerRef.current,
      style:              resolvedStyle as string | StyleSpecification,
      center:             getCenter(),
      zoom:               initialZoom,
      attributionControl: false,
    });

    mapRef.current = map;

    /* zoom custom */
    const zoomDiv = document.createElement('div');
    zoomDiv.className = styles.customZoom;
    zoomDiv.innerHTML = `<button id="zin">+</button><button id="zout">−</button>`;
    containerRef.current.appendChild(zoomDiv);
    zoomDiv.querySelector('#zin')!.addEventListener('click',  () => map.zoomIn());
    zoomDiv.querySelector('#zout')!.addEventListener('click', () => map.zoomOut());

    /* molette après un clic seulement : sinon la page défile, et un mot le rappelle */
    if (clickToZoom) {
      const container = containerRef.current;
      const hint = document.createElement('div');
      hint.textContent = 'Cliquez dans la carte pour zoomer avec la molette';
      Object.assign(hint.style, {
        position: 'absolute', inset: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(11, 21, 40, 0.55)', color: '#FFFFFF', fontSize: '0.95rem', zIndex: '600',
        pointerEvents: 'none', opacity: '0', transition: 'opacity 0.2s',
      });
      container.appendChild(hint);
      let hintTimer: ReturnType<typeof setTimeout> | undefined;
      map.scrollZoom.disable();
      container.addEventListener('mousedown', () => { map.scrollZoom.enable(); hint.style.opacity = '0'; });
      container.addEventListener('mouseleave', () => map.scrollZoom.disable());
      container.addEventListener('wheel', () => {
        if (map.scrollZoom.isEnabled()) return;
        hint.style.opacity = '1';
        clearTimeout(hintTimer);
        hintTimer = setTimeout(() => { hint.style.opacity = '0'; }, 1200);
      }, { passive: true });
    }

    /* canvas heatmap */
    if (renderMode === 'dark') {
      const canvas = document.createElement('canvas');
      canvas.className = styles.heatmapCanvas;
      containerRef.current.appendChild(canvas);
      canvasRef.current = canvas;
    }

    map.on('load', () => {
      if (mapColors) applyMapColors(map, mapColors);
      if (cityLabelsOnly) applyCityLabelsOnly(map);
      syncMarkers();
      redrawCanvas();
      cadrerRef.current?.(false);
    });

    // Ces listeners capturent redrawCanvas/syncMarkers une seule fois,
    // mais comme ces fonctions lisent coordinatesRef.current, elles auront
    // toujours les données à jour — plus de closure périmée sur [].
    // Point le plus proche du curseur (dans un rayon de 18 px).
    let pointSurvole: number | null = null;
    const signaler = (index: number | null) => {
      if (index === pointSurvole) return;
      pointSurvole = index;
      onPointHoverRef.current?.(index);
    };
    const plusProche = (point: { x: number; y: number }) => {
      let best: number | null = null;
      let bestD = 18 * 18;
      coordinatesRef.current.forEach((c, i) => {
        const p = map.project([c.lng, c.lat]);
        const d = (p.x - point.x) ** 2 + (p.y - point.y) ** 2;
        if (d <= bestD) { bestD = d; best = i; }
      });
      return best;
    };
    map.on('mousemove', (e) => {
      if (!onPointHoverRef.current && !onPointClickRef.current) return;
      const best = plusProche(e.point);
      if (onPointClickRef.current) map.getCanvas().style.cursor = best === null ? '' : 'pointer';
      signaler(best);
    });
    map.on('click', (e) => { onPointClickRef.current?.(plusProche(e.point)); });
    map.getCanvas().addEventListener('mouseleave', () => signaler(null));

    map.on('move',   redrawCanvas);
    map.on('zoom',   redrawCanvas);
    map.on('resize', redrawCanvas);

    // Dès que l'utilisateur déplace ou zoome lui-même, on ne recadre plus sur un simple
    // redimensionnement (seul un changement de points le refait).
    map.on('dragstart', () => { utilisateurABouge.current = true; });
    map.on('zoomstart', (e: { originalEvent?: unknown }) => { if (e.originalEvent) utilisateurABouge.current = true; });

    // Le conteneur n'a souvent sa taille définitive qu'après le premier rendu : on
    // recadre alors, sinon la vue se calcule sur une carte mal dimensionnée.
    const resizeObserver = new ResizeObserver(() => {
      map.resize();
      if (!utilisateurABouge.current && map.isStyleLoaded()) cadrerRef.current?.(false);
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      markersRef.current.forEach(m => m.remove());
      map.remove();
      mapRef.current    = null;
      canvasRef.current = null;
    };
  }, [resolvedStyle, initialZoom]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- re-sync markers/canvas quand les coords ou le mode changent ---- */
  // Un point qui devient `selected` ne déplace rien : on redessine sans recadrer.
  const positionsRef = useRef('');
  useEffect(() => {
    if (!mapRef.current) return;
    if (mapRef.current.isStyleLoaded()) {
      syncMarkers();
      redrawCanvas();
      const positions = `${renderMode}|${autoCenter}|${fitPoints}|${emptyCenter?.lat},${emptyCenter?.lng}|` + coordinates.map(c => `${c.lat},${c.lng}`).join(';');
      const memes = positions === positionsRef.current;
      positionsRef.current = positions;
      if (!memes || markers) cadrerRef.current?.(true);
    }
  }, [coordinates, markers, renderMode, autoCenter, fitPoints, emptyCenter, syncMarkers, redrawCanvas, getCenter]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- appliquer mapColors quand elles changent ---- */
  useEffect(() => {
    if (!mapRef.current || !mapColors) return;
    if (mapRef.current.isStyleLoaded()) applyMapColors(mapRef.current, mapColors);
  }, [mapColors]);

  /* ---- appliquer cityLabelsOnly quand la prop change ---- */
  useEffect(() => {
    if (!mapRef.current || !cityLabelsOnly) return;
    if (mapRef.current.isStyleLoaded()) applyCityLabelsOnly(mapRef.current);
  }, [cityLabelsOnly]);

  return (
    <div
      ref={containerRef}
      className={styles.mapContainer}
      style={{ width, height }}
    />
  );
};

export default OpenStreetMapV2;